// src/ecrans/accueil/accueil-calculs.js — ce que l'Accueil NEST affiche,
// calculé depuis les réponses des portes (fonctions pures, testées).
//
// Une seule source par chiffre : le nombre d'une tuile EST la longueur de la
// liste qu'elle déplie.

import {
  demandesHoraire,
  signalementsVoyageur,
  veillesOperateur,
} from "../../lib/accueil-attend";
import { coutSiRate, trierParUrgence } from "../../lib/accueil-urgence";
import { pl } from "../nest";

/** Le premier jour de la réponse de `ecran_journee_lire` (ou null). */
export function jourDe(journee) {
  return journee && Array.isArray(journee.jours) && journee.jours[0]
    ? journee.jours[0]
    : null;
}

const nomLog = (l) => (l && l.nom) || "Logement inconnu";

/** Ménages du jour → lignes du détail de la tuile. */
export function lignesMenages(jour) {
  return ((jour && jour.menages) || []).map((m) => {
    const qui = m.agent && m.agent.prenom ? m.agent.prenom : "personne dessus";
    const heure = m.heure ? String(m.heure).slice(0, 5).replace(":", "h") : null;
    return {
      cle: `menage:${m.id}`,
      ic: m.fini ? "✓" : m.agent ? "◷" : "!",
      ton: m.fini ? "vert" : m.agent ? "gris" : "aub",
      titre: nomLog(m.logement),
      sous: [qui, heure, m.fini ? "fait" : null].filter(Boolean).join(" · "),
    };
  });
}

/** Arrivées ou départs du jour → lignes du détail. */
export function lignesMouvements(jour, sens) {
  const liste = (jour && (sens === "arrivee" ? jour.arrivees : jour.departs)) || [];
  return liste.map((s) => ({
    cle: `${sens}:${s.type}:${s.id}`,
    ic: sens === "arrivee" ? "↑" : "↓",
    ton: sens === "arrivee" ? "vert" : "aub",
    titre: nomLog(s.logement),
    sous: [s.voyageur || "Voyageur", libellePlateforme(s.source), s.heure ? heureH(s.heure) : null]
      .filter(Boolean)
      .join(" · "),
  }));
}

export function heureH(h) {
  const t = String(h || "").slice(0, 5);
  if (!/^\d{1,2}:\d{2}$/.test(t)) return t || null;
  const [hh, mm] = t.split(":");
  return mm === "00" ? `${Number(hh)}h` : `${Number(hh)}h${mm}`;
}

export function libellePlateforme(source) {
  const s = String(source || "").toLowerCase();
  if (s.includes("airbnb")) return "Airbnb";
  if (s.includes("booking")) return "Booking";
  if (s === "direct" || s.includes("site")) return "Direct";
  return null;
}

/** Les ménages du jour sans agent et pas finis : ils attendent un geste. */
export function menagesSansAgent(jour) {
  return ((jour && jour.menages) || [])
    .filter((m) => !m.fini && !m.agent)
    .map((m) => ({
      cle: `sans-agent:${m.id}`,
      nature: "menage_sans_agent",
      ic: "menage",
      quand: m.heure ? `à ${heureH(m.heure)}` : null,
      ton: "aub",
      titre: "Un ménage sans agent",
      sousTitre: [nomLog(m.logement), m.heure ? `à ${heureH(m.heure)}` : null]
        .filter(Boolean)
        .join(" · "),
      cible: { ecran: "menages" },
    }));
}

/** Un incident ouvert et personne n'a tranché (porte `ecran_incidents_lire`). */
export function incidentsAJuger(incidents) {
  return ((incidents && incidents.a_juger) || []).map((i) => ({
    cle: `incident:${i.id}`,
    nature: "incident_a_trancher",
    ic: "incident",
    ton: "aub",
    titre: "Un incident à trancher",
    sousTitre: [nomLog(i.logement), i.titre].filter(Boolean).join(" · "),
    cible: { ecran: "incidents" },
  }));
}

/** Les reprises ouvertes (porte `ecran_reprises_lire`). */
export function reprisesOuvertes(reprises) {
  return ((reprises && reprises.reprises) || []).map((r) => ({
    cle: `reprise:${r.id}`,
    nature: "reprise_en_attente",
    ic: "controle",
    ton: "aub",
    titre: "Une reprise en attente",
    sousTitre: [
      nomLog(r.logement),
      r.reprise_par === "controleur"
        ? `${(r.controleur && r.controleur.prenom) || "le contrôleur"} doit repasser`
        : `${(r.agent && r.agent.prenom) || "l'agent"} doit repasser`,
    ].join(" · "),
    cible: { ecran: "menage-controle" },
  }));
}

/** Les contrôles à faire que personne n'a sur sa liste (`ecran_controles_lire`). */
export function controlesSansPersonne(controles) {
  return ((controles && controles.a_faire) || [])
    .filter((c) => !c.controleur)
    .map((c) => ({
      cle: `ctrl-orphelin:${c.menage_id}`,
      nature: "controle_sans_personne",
      ic: "controle",
      ton: "gris",
      titre: "Un contrôle sans personne",
      sousTitre: nomLog(c.logement),
      cible: { ecran: "menage-controle" },
    }));
}

/**
 * Les fermetures de dates refusées par une plateforme, ou restées sans réponse
 * (porte `ecran_fermetures_lire`, état « echec ») : à reprendre. Celles « en
 * cours » n'attendent aucune décision : elles ne sont pas listées.
 */
export function fermeturesAReprendre(fermetures) {
  return ((fermetures && fermetures.fermetures) || [])
    .filter((f) => f && f.etat === "echec")
    .map((f) => {
      const pf = libellePlateforme(f.plateforme) || f.plateforme || null;
      const quoi = f.geste === "rouvrir" ? "Rouvrir les dates" : "Fermer les dates";
      return {
        cle: `fermeture:${f.id}`,
        nature: "fermeture_a_reprendre",
        ic: "maison",
        ton: "aub",
        titre: `${quoi}${pf ? ` sur ${pf}` : ""} : à reprendre`,
        sousTitre: [nomLog(f.logement), pf ? `${pf} a refusé ou n'a pas répondu` : "la plateforme a refusé ou n'a pas répondu"].join(" · "),
        quand: Number.isFinite(f.essais) && f.essais > 0 ? pl(f.essais, "échec") : null,
        cible: { ecran: "calendrier" },
      };
    });
}

const ICONE_DE_NATURE = {
  demande_horaire: "horloge",
  signalement_voyageur: "incident",
};

/**
 * « À décider » : ménages sans agent, demandes d'horaire, signalements de
 * voyageurs, incidents à trancher, reprises, contrôles sans personne,
 * fermetures à reprendre, et les veilles de l'opérateur (« repéré par
 * l'opérateur »). `complet` : faux tant qu'une lecture manque ou a raté, et
 * le compteur dit alors « — ». HORS COMPTE (argent, couloir A) : les réponses
 * AirCover à saisir, que la porte des incidents ne rend pas.
 */
export function ceQuiVousAttend({ journee, horaires, signalements, incidents, reprises, controles, fermetures, veilles, nomLogement }) {
  const lignes = [];
  const sources = [journee, horaires, signalements, incidents, reprises, controles, fermetures, veilles];
  const lue = (s) => s && s.etat === "ok";
  if (lue(fermetures)) lignes.push(...fermeturesAReprendre(fermetures.data));
  if (lue(incidents)) lignes.push(...incidentsAJuger(incidents.data));
  if (lue(journee)) lignes.push(...menagesSansAgent(jourDe(journee.data)));
  if (lue(reprises)) lignes.push(...reprisesOuvertes(reprises.data));
  if (lue(controles)) lignes.push(...controlesSansPersonne(controles.data));
  if (lue(horaires))
    lignes.push(
      ...demandesHoraire(horaires.data.pending || [], { nomLogement }).map((l) => ({
        ...l,
        ic: ICONE_DE_NATURE[l.nature],
        ton: "gris",
      })),
    );
  if (lue(signalements) && signalements.data.actif !== false)
    lignes.push(
      ...signalementsVoyageur(signalements.data.signalements || [], { nomLogement }).map((l) => ({
        ...l,
        ic: ICONE_DE_NATURE[l.nature],
        ton: "aub",
      })),
    );
  if (lue(veilles))
    lignes.push(
      ...veillesOperateur(veilles.data.alertes || [], { nomLogement }).map((l) => ({
        ...l,
        ic: "oeil",
        operateur: true,
        cible: { ecran: "operateur" },
      })),
    );
  const echecs = sources.filter((s) => s && s.etat === "echec").length;
  const enLecture = sources.some((s) => !s || s.etat === "lecture");
  return { lignes, echecs, enLecture, complet: !enLecture && !echecs };
}

/**
 * Les trois groupes de « À décider », par le barème existant « que coûte-t-il
 * de le rater ? » (src/lib/accueil-urgence.js) : 0-9 Urgent, 10-19
 * Aujourd'hui, 20 et plus Peut attendre. Chaque groupe est trié par le même
 * barème (`trierParUrgence`) ; un groupe vide n'est pas rendu.
 */
export const GROUPES = [
  { cle: "urgent", titre: "Urgent", garde: (c) => c < 10 },
  { cle: "aujourdhui", titre: "Aujourd'hui", garde: (c) => c >= 10 && c < 20 },
  { cle: "attendre", titre: "Peut attendre", garde: (c) => c >= 20 },
];

export function groupesADecider(lignes) {
  const triees = trierParUrgence(lignes || []);
  return GROUPES.map((g) => ({ ...g, lignes: triees.filter((l) => g.garde(coutSiRate(l))) })).filter(
    (g) => g.lignes.length,
  );
}

/** Les tâches de la semaine : en retard + 7 jours + sans date. */
export function tachesAFaire(semaine) {
  if (!semaine) return [];
  const jours = Array.isArray(semaine.jours) ? semaine.jours : [];
  return [
    ...(semaine.en_retard || []).map((t) => ({ ...t, retard: true })),
    ...jours.flatMap((j) => j.taches || []),
    ...(semaine.sans_date || []),
  ];
}

/** Une ligne de message en attente (porte `ecran_conversations_lire`). */
export function ligneFil(f) {
  const attente =
    Number.isFinite(f.attente_depuis_jours) && f.attente_depuis_jours > 0
      ? `attend depuis ${f.attente_depuis_jours} j`
      : "attend une réponse";
  return {
    cle: `fil:${f.thread_id}`,
    titre: f.voyageur || "Voyageur",
    sous: [f.logement ? f.logement.nom : null, attente].filter(Boolean).join(" · "),
    extrait: f.extrait || "",
  };
}

/**
 * La ligne « à traiter » : propositions + à décider + messages. Tant que « à
 * décider » est incomplet (lecture ratée ou en cours), le total vaut « — ».
 */
export function aTraiter({ nbPropositions, attend, nbMessages }) {
  const p = Number.isFinite(nbPropositions) ? nbPropositions : null;
  const m = Number.isFinite(nbMessages) ? nbMessages : null;
  const dec = attend && attend.complet && !attend.echecs ? attend.lignes.length : null;
  const total = p !== null && m !== null && dec !== null ? p + m + dec : null;
  const compte = (n, un, plusieurs) => (n === null ? `— ${plusieurs}` : pl(n, un, plusieurs));
  return {
    total,
    detail: [
      compte(p, "proposition", "propositions"),
      dec === null ? "— à décider" : `${dec} à décider`,
      compte(m, "message", "messages"),
    ].join(" · "),
  };
}
